import type { ReactNode } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Body, Card, Eyebrow, PersonRowSkeleton, Row, Skeleton, Stack } from '@leanin/ui';
import { MemberRow } from '../MemberRow';
import type { PersonSummary } from './types';

export function GroupPeopleListCard({
  title,
  totalCount,
  people,
  visibleCount,
  emptyMessage,
  headerAction,
  footerAction,
  renderRowAction,
  frameless = false,
  loading = false,
  loadingRowCount = 5,
}: {
  title: string;
  totalCount?: number;
  people: ReadonlyArray<PersonSummary>;
  visibleCount?: number;
  emptyMessage?: string;
  headerAction?: ReactNode;
  footerAction?: ReactNode;
  renderRowAction?: (person: PersonSummary) => ReactNode;
  /** Render without the Card frame — for asides where a flush flow
   *  reads better than another stacked rectangle. */
  frameless?: boolean;
  /** Render skeleton rows in place of `people`. The heading still renders
   *  (with a skeleton count badge), so the card chrome is stable from
   *  loading through loaded. */
  loading?: boolean;
  /** When loading, how many skeleton rows to render. Defaults to 5 to
   *  match the typical `visibleCount`. */
  loadingRowCount?: number;
}) {
  const t = useTranslations('Group');
  const resolvedEmptyMessage = emptyMessage ?? t('members.emptyNoOne');
  const visible = visibleCount !== undefined ? people.slice(0, visibleCount) : people;
  const heading: ReactNode = loading ? (
    <Row gap="sm" align="baseline">
      <Eyebrow tone="accent">{title}</Eyebrow>
      <Skeleton width={24} height={14} rounded="sm" />
    </Row>
  ) : (
    <Eyebrow tone="accent">
      {totalCount !== undefined ? `${title} (${totalCount})` : title}
    </Eyebrow>
  );

  const inner = (
    <Stack gap="md">
      <Row justify="space-between" align="baseline">
        {heading}
        {headerAction}
      </Row>
      {loading ? (
        <Stack gap="sm">
          {Array.from({ length: loadingRowCount }, (_, i) => (
            <PersonRowSkeleton key={i} avatarSize={32} />
          ))}
        </Stack>
      ) : visible.length === 0 ? (
        <Body size="sm" color="mute">{resolvedEmptyMessage}</Body>
      ) : (
        <Stack gap="sm">
          {visible.map((p) => (
            <MemberRow
              key={p.id}
              userId={p.id}
              photo={p.photo}
              name={p.name}
              sublabel={p.sublabel}
              avatarSize={32}
              {...(p.href ? { href: p.href } : {})}
              {...(renderRowAction ? { right: renderRowAction(p) } : {})}
            />
          ))}
        </Stack>
      )}
      {footerAction}
    </Stack>
  );
  if (frameless) {
    return <Stack px="md">{inner}</Stack>;
  }
  return <Card pad="default">{inner}</Card>;
}
